import { useState } from "react";
import { Link } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Crown, ExternalLink, Globe } from "lucide-react";
import type { PublishIssue, PublishResponse } from "@baze/api-client";
import { Button, buttonClass } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Alert } from "@/components/ui/alert";
import { CopyButton } from "@/components/CopyButton";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { Spinner } from "@/components/ui/spinner";
import { api, errorMessage } from "@/lib/api";
import { qk } from "@/lib/query";
import { eventIds, trackCustom } from "@/lib/meta-pixel";
import { t } from "@/i18n/id";
import { displayUrl } from "@/features/sites/SiteStatus";
import { PromoCallout, promoFor, usePromos, useUnlock } from "@/features/commerce/promos";

type Phase = "checking" | "ready" | "publishing" | "success" | "error";

export function PublishDialog({
  open,
  onOpenChange,
  siteId,
  dirty,
  save,
  onFix,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  siteId: string;
  dirty: boolean;
  save: () => Promise<void>;
  onFix: (issue: PublishIssue) => void;
}) {
  const qc = useQueryClient();
  const promos = usePromos();
  const unlockItems = useUnlock(siteId);
  const [wasOpen, setWasOpen] = useState(false);
  const [tick, setTick] = useState(0);
  const [result, setResult] = useState<PublishResponse | null>(null);
  const [error, setError] = useState("");
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setTick((n) => n + 1);
      setResult(null);
      setError("");
    }
  }

  const resend = useMutation({
    mutationFn: () => api.auth.resendVerification(),
    meta: { success: t.auth.verifyResent },
  });

  const checkQuery = useQuery({
    queryKey: [...qk.sites.publishCheck(siteId), tick],
    enabled: open && tick > 0 && !result,
    queryFn: async () => {
      if (dirty) await save();
      return api.sites.publishCheck(siteId);
    },
  });

  const publish = useMutation({
    mutationFn: () => api.sites.publish(siteId),
    meta: { silent: true },
    onSuccess: (res) => {
      setResult(res);
      trackCustom("Publish", { site_id: siteId }, eventIds.publish(siteId));
      qc.setQueryData(qk.sites.detail(siteId), res.site);
      void qc.invalidateQueries({ queryKey: qk.sites.list });
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const check = checkQuery.data ?? null;
  const phase: Phase = result
    ? "success"
    : publish.isPending
      ? "publishing"
      : error || checkQuery.isError
        ? "error"
        : check
          ? "ready"
          : "checking";

  const blockers = check?.blockers ?? [];
  // Premium template / theme: go straight to checkout with every locked item and the promo already applied.
  const paywalled = blockers.filter((b) => !!b.purchase);
  const otherBlockers = blockers.filter((b) => !b.purchase);
  const promo = promoFor(promos.data, paywalled.flatMap((b) => (b.purchase ? [b.purchase.kind] : [])));
  const unlock = (issues: PublishIssue[]) => {
    const items = issues.flatMap((b) => (b.purchase ? [b.purchase] : []));
    if (!items.length) return;
    onOpenChange(false);
    unlockItems(items, promo);
  };
  const warnings = check?.warnings ?? [];
  const url = result?.site.primaryUrl;
  const share = url ? `https://wa.me/?text=${encodeURIComponent(t.editor.publishDialog.shareMessage(result?.site.name ?? "", url))}` : "";

  const footer =
    phase === "ready" ? (
      <Button disabled={blockers.length > 0} loading={publish.isPending} onClick={() => publish.mutate()}>
        {warnings.length > 0 && blockers.length === 0 ? t.editor.publishDialog.publishAnyway : t.editor.publishDialog.publishNow}
      </Button>
    ) : phase === "error" ? (
      <Button variant="secondary" onClick={() => onOpenChange(false)}>
        {t.common.close}
      </Button>
    ) : phase === "success" && url ? (
      <>
        <Link to={`/situs/${siteId}/domain`} className={buttonClass("secondary", "md")} onClick={() => onOpenChange(false)}>
          <Globe aria-hidden="true" />
          {t.editor.publishDialog.connectDomain}
        </Link>
        <a href={url} target="_blank" rel="noopener noreferrer" className={buttonClass("primary", "md")}>
          <ExternalLink aria-hidden="true" />
          {t.editor.publishDialog.viewSite}
        </a>
      </>
    ) : null;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && phase === "publishing") return; onOpenChange(next); }}>
      <DialogContent
        title={phase === "success" ? (result?.unchanged ? t.editor.publishDialog.unchanged : t.editor.publishDialog.successTitle) : t.editor.publishDialog.title}
        description={phase === "success" ? (result?.unchanged ? t.editor.publishDialog.unchangedBody : t.editor.publishDialog.successBody) : undefined}
        size="md"
        footer={footer}
      >
        {phase === "checking" || phase === "publishing" ? (
          <div className="flex items-center gap-3 py-6 text-sm font-medium text-slate-600">
            <Spinner />
            {phase === "publishing" ? t.editor.publishDialog.publishing : dirty ? t.editor.publishDialog.saving : t.editor.publishDialog.checking}
          </div>
        ) : null}

        {phase === "error" ? <Alert tone="danger">{error || errorMessage(checkQuery.error) || t.editor.publishDialog.checkFailed}</Alert> : null}

        {phase === "ready" && check ? (
          <div className="space-y-3">
            {blockers.length === 0 && warnings.length === 0 ? (
              <Alert tone="success" title={t.editor.publishDialog.ready}>
                {t.editor.publishDialog.readyBody}
              </Alert>
            ) : null}
            {paywalled.length > 0 ? (
              <div className="space-y-3 rounded-2xl bg-amber-50/70 p-4 ring-1 ring-inset ring-amber-200">
                <div className="flex items-start gap-3">
                  <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-amber-100 text-amber-700">
                    <Crown className="size-[18px]" aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-slate-900">{t.editor.publishDialog.premiumTitle}</p>
                    <ul className="mt-1 space-y-0.5 text-[13px] text-slate-600">
                      {paywalled.map((b) => (
                        <li key={`${b.purchase?.kind}-${b.purchase && "id" in b.purchase ? b.purchase.id : ""}`}>{b.message}</li>
                      ))}
                    </ul>
                  </div>
                </div>
                {promo ? <PromoCallout promo={promo} /> : null}
                <div className="flex flex-wrap items-center gap-2">
                  <Button size="sm" onClick={() => unlock(paywalled)} leftIcon={<Crown aria-hidden="true" />}>
                    {promo ? t.editor.publishDialog.premiumCtaPromo : t.editor.publishDialog.premiumCta}
                  </Button>
                  <span className="text-xs text-slate-500">{t.editor.publishDialog.premiumOnce}</span>
                </div>
              </div>
            ) : null}
            {otherBlockers.length > 0 ? (
              <IssueList title={t.editor.publishDialog.blockers} tone="danger" issues={otherBlockers} onFix={onFix} onResend={() => resend.mutate()} resending={resend.isPending} />
            ) : null}
            {warnings.length > 0 ? (
              <IssueList title={t.editor.publishDialog.warnings} hint={t.editor.publishDialog.warningsHint} tone="warning" issues={warnings} onFix={onFix} />
            ) : null}
          </div>
        ) : null}

        {phase === "success" && url ? (
          <div className="space-y-4">
            <a href={url} target="_blank" rel="noopener noreferrer" className="block break-all rounded-2xl bg-slate-50 px-4 py-3 text-base font-bold text-slate-900 ring-1 ring-slate-900/8 hover:text-primary">
              {displayUrl(url)}
            </a>
            <p className="text-sm leading-relaxed text-slate-500">{t.editor.publishDialog.domainAfter}</p>
            <div className="flex flex-wrap gap-2">
              <CopyButton value={url} label={t.common.copyLink} />
              <a href={share} target="_blank" rel="noopener noreferrer" className={buttonClass("whatsapp", "sm")}>
                <WhatsAppIcon />
                {t.editor.publishDialog.share}
              </a>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function IssueList({
  title,
  hint,
  tone,
  issues,
  onFix,
  onBuy,
  onResend,
  resending,
}: {
  title: string;
  hint?: string;
  tone: "danger" | "warning";
  issues: PublishIssue[];
  onFix: (issue: PublishIssue) => void;
  onBuy?: (issue: PublishIssue) => void;
  onResend?: () => void;
  resending?: boolean;
}) {
  return (
    <div className="space-y-2">
      <div>
        <p className="text-sm font-semibold text-slate-900">{title}</p>
        {hint ? <p className="text-xs text-slate-500">{hint}</p> : null}
      </div>
      <ul className="space-y-2">
        {issues.map((issue, i) => (
          <li key={`${issue.code}-${issue.sectionId ?? i}`}>
            <Alert
              tone={tone}
              action={
                issue.purchase && onBuy ? (
                  <Button size="sm" variant="secondary" onClick={() => onBuy(issue)}>
                    {t.editor.publishDialog.paywallCta}
                  </Button>
                ) : issue.code === "email_not_verified" && onResend ? (
                  <Button size="sm" variant="secondary" loading={resending} onClick={onResend}>
                    {t.auth.verifyResend}
                  </Button>
                ) : issue.sectionId ? (
                  <Button size="sm" variant="secondary" onClick={() => onFix(issue)}>
                    {t.editor.publishDialog.fix}
                  </Button>
                ) : null
              }
            >
              {issue.message}
            </Alert>
          </li>
        ))}
      </ul>
    </div>
  );
}
